// GlobalContextProvider.tsx

import React, { createContext, useContext, useReducer, Dispatch } from 'react';
import i18n from 'i18next';
import { PrintConnectIndustry } from '../models/Industry';
import { UserInfoResponse } from '../models/User';
import { Registration } from '../models/Registration';
import { trackErrorViewed } from '../services/analytics';

interface ErrorLog {
    show: boolean;
    type: string;
    displayErrorMessage: string;
    rawErrorMessage?: any; // rawErrorMessage is optional
}

interface GlobalState {
    locale: string;
    registrationFormData: Registration;
    industries: PrintConnectIndustry[];
    error: ErrorLog;
    currentStoreLocationNumber: string;
    userInfoResponse: UserInfoResponse | undefined
    isEmployeeIDFlagEnabled: boolean;
    isPreferredStoreFlagEnabled: boolean;
    isKioskUser: boolean;
}

type GlobalContextProviderProps = {
    children: React.ReactNode
}

type GlobalAction =
    | { type: 'TOGGLE_LOCALE' }
    | { type: 'CHANGE_REGISTRATION_FORM'; field: string; value: string }
    | { type: 'LOG_ERROR'; error: ErrorLog }
    | { type: 'CURRENT_STORE_NUMBER'; currentStoreLocationNumber: string }
    | { type: 'LOGIN_USER_INFO'; userInfo: UserInfoResponse }
    | { type: 'SET_INDUSTRIES'; industries: PrintConnectIndustry[] }
    | { type: 'SET_LOCALE'; locale: string }
    | { type: 'SET_IS_EMPLOYEE_ID_FLAG_ENABLED'; isEmployeeIDFlagEnabled: boolean }
    | { type: 'SET_IS_PREFERRED_STORE_FLAG_ENABLED'; isPreferredStoreFlagEnabled: boolean }
    | { type: 'SET_IS_KIOSK_USER'; isKioskUser: boolean };

const initialState: GlobalState = {
    locale: i18n.language === 'en' || i18n.language === 'en-CA' || i18n.language === 'en-CA' ? 'en-CA' : 'fr-CA',
    registrationFormData: {
        firstName: '',
        lastName: '',
        jobTitle: '',
        phoneNumber: '',
        email: '',
        membershipType: '',
        useType: '',
        companyName: '',
        industry: '',
        preferredStoreId: '',
        addressLineOne: '',
        addressLineTwo: '',
        cityName: '',
        province: '',
        postalCode: '',
        cultureName: '',
        numberOfEmployees: '',
        employeeID: '',
        bdpNumber: '',
    },
    industries: [],
    error: {
        show: false, type: '', displayErrorMessage: '', rawErrorMessage: null
    },
    currentStoreLocationNumber: 'S999',
    userInfoResponse: undefined,
    isEmployeeIDFlagEnabled: false,
    isPreferredStoreFlagEnabled: false,
    isKioskUser: false,
}

const GlobalStateContext = createContext<{ state: GlobalState; dispatch: Dispatch<GlobalAction> } | undefined>(undefined);

const globalReducer = (state: GlobalState, action: GlobalAction): GlobalState => {
    switch (action.type) {
        case 'TOGGLE_LOCALE': {
            const newLocale = state.locale === 'en-CA' ? 'fr-CA' : 'en-CA';
            i18n.changeLanguage(newLocale === 'en-CA' ? 'en' : 'fr');
            return { ...state, locale: newLocale };
        }
        case 'CHANGE_REGISTRATION_FORM':
            return {
                ...state,
                registrationFormData: {
                    ...state.registrationFormData,
                    [action.field]: action.value
                }
            };
        case 'LOG_ERROR': {
            // Fire Segment Error for global errors
            if (action.error && action.error.show && action.error.displayErrorMessage) {
                try {
                    trackErrorViewed(action.error.displayErrorMessage);
                } catch (e) {
                    console.error('Failed to track error viewed', e);
                }
            }
            return {
                ...state,
                error: action.error,
            };
        }
        case 'CURRENT_STORE_NUMBER': {
            // const parsedCurrentStoreLocationNumber = `S${action.currentStoreLocationNumber.toString().padStart(3, "0")}`
            return {
                ...state,
                currentStoreLocationNumber: action.currentStoreLocationNumber.toString()
            };
        }
        case 'LOGIN_USER_INFO':
            return {
                ...state,
                userInfoResponse: action.userInfo,
                registrationFormData: {
                    ...state.registrationFormData,
                    firstName: action.userInfo.FirstName,
                    lastName: action.userInfo.LastName,
                    email: action.userInfo.Email
                }
            };
        case 'SET_LOCALE': {
            i18n.changeLanguage(action.locale === 'en-CA' ? 'en' : 'fr');
            return { ...state, locale: action.locale };
        }
        case 'SET_INDUSTRIES':
            return {
                ...state,
                industries: action.industries,
            };
        case 'SET_IS_EMPLOYEE_ID_FLAG_ENABLED':
            return {
                ...state,
                isEmployeeIDFlagEnabled: action.isEmployeeIDFlagEnabled,
            };
        case 'SET_IS_PREFERRED_STORE_FLAG_ENABLED':
            return {
                ...state,
                isPreferredStoreFlagEnabled: action.isPreferredStoreFlagEnabled,
            };
        case 'SET_IS_KIOSK_USER':
            return {
                ...state,
                isKioskUser: action.isKioskUser,
            };
        default:
            return state;
    }
};

const GlobalContextProvider = (props: GlobalContextProviderProps) => {
    const [state, dispatch] = useReducer(globalReducer, initialState);
    return (
        <GlobalStateContext.Provider value={{ state, dispatch }}>
            {props.children}
        </GlobalStateContext.Provider>
    );
};

const useGlobalContext = () => {
    const context = useContext(GlobalStateContext);
    if (!context) {
        throw new Error('useGlobalContext must be used within a GlobalContextProvider');
    }
    return context;
};

export { GlobalContextProvider, useGlobalContext };